import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "../components/ui/sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

// ── Widget Tab — embeddable JS snippet config for the estudio's website. ──
function WidgetTab({ form, setForm, config, onSave, saving }) {
  const slug = (form.widget_slug || "").trim();
  const origin = (typeof window !== "undefined" && window.location.origin) || "https://acbothub.com";
  const snippet = slug
    ? `<script src="${origin}/widget-legal.js"\n        data-slug="${slug}"\n        data-color="${form.widget_color || "#7c3aed"}"\n        async></script>`
    : "(define un Widget slug y guarda la configuración para ver el código)";

  const [copied, setCopied] = React.useState(false);
  async function copySnippet() {
    if (!slug) {
      toast.error("Primero define un Widget slug y guarda la configuración.");
      return;
    }
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success("Snippet copiado");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("No se pudo copiar — selecciona y copia manualmente.");
    }
  }

  return (
    <div className="space-y-5" data-testid="legal-widget-tab">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-sm text-purple-900">
        <strong className="font-semibold">Widget embebible para tu sitio web.</strong>{" "}
        Pega el snippet de abajo justo antes de <code className="bg-purple-100 px-1 rounded">{`</body>`}</code> en cualquier página de tu sitio. Tu Renata aparecerá como un botón flotante (esquina inferior derecha).
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Widget slug <span className="text-xs text-gray-500">(URL pública)</span></label>
          <input
            type="text"
            value={form.widget_slug || ""}
            onChange={e => setForm(f => ({ ...f, widget_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-").slice(0, 64) }))}
            placeholder="estudio-vargas"
            data-testid="legal-input-widget_slug"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono"
          />
          <p className="mt-1 text-xs text-gray-500">Solo letras minúsculas, números y guiones. Ej: <code className="bg-gray-100 px-1 rounded">estudio-vargas</code>.</p>
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
            <input
              type="checkbox"
              checked={form.widget_enabled !== false}
              onChange={e => setForm(f => ({ ...f, widget_enabled: e.target.checked }))}
              data-testid="legal-input-widget_enabled"
              className="rounded"
            />
            Widget activo
          </label>
          <p className="text-xs text-gray-500 ml-6">Apaga este toggle si quieres ocultar temporalmente el botón flotante de tu sitio.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Color corporativo (botón flotante)</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={form.widget_color || "#7c3aed"}
              onChange={e => setForm(f => ({ ...f, widget_color: e.target.value }))}
              data-testid="legal-input-widget_color"
              className="h-10 w-14 rounded border cursor-pointer"
            />
            <input
              type="text"
              value={form.widget_color || ""}
              onChange={e => setForm(f => ({ ...f, widget_color: e.target.value }))}
              placeholder="#7c3aed"
              className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Color del texto (sobre el botón)</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={form.widget_text_color || "#ffffff"}
              onChange={e => setForm(f => ({ ...f, widget_text_color: e.target.value }))}
              data-testid="legal-input-widget_text_color"
              className="h-10 w-14 rounded border cursor-pointer"
            />
            <input
              type="text"
              value={form.widget_text_color || ""}
              onChange={e => setForm(f => ({ ...f, widget_text_color: e.target.value }))}
              placeholder="#ffffff"
              className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Color secundario <span className="text-xs text-gray-500 font-normal">(halo + ring del input)</span>
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={form.widget_secondary_color || "#a78bfa"}
              onChange={e => setForm(f => ({ ...f, widget_secondary_color: e.target.value }))}
              data-testid="legal-input-widget_secondary_color"
              className="h-10 w-14 rounded border cursor-pointer"
            />
            <input
              type="text"
              value={form.widget_secondary_color || ""}
              onChange={e => setForm(f => ({ ...f, widget_secondary_color: e.target.value }))}
              placeholder="#a78bfa"
              className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Color del header <span className="text-xs text-gray-500 font-normal">(vacío = usa el color principal)</span>
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={form.widget_header_color || form.widget_color || "#7c3aed"}
              onChange={e => setForm(f => ({ ...f, widget_header_color: e.target.value }))}
              data-testid="legal-input-widget_header_color"
              className="h-10 w-14 rounded border cursor-pointer"
            />
            <input
              type="text"
              value={form.widget_header_color || ""}
              onChange={e => setForm(f => ({ ...f, widget_header_color: e.target.value }))}
              placeholder="Vacío = usa color principal"
              className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono"
            />
            {form.widget_header_color && (
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, widget_header_color: "" }))}
                className="text-xs text-gray-500 hover:text-red-600 px-2 py-1"
                data-testid="legal-clear-header-color-btn"
                title="Volver al color principal"
              >
                Limpiar
              </button>
            )}
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            URL del avatar del asistente <span className="text-xs text-gray-500 font-normal">(opcional, https://… imagen cuadrada idealmente 200×200)</span>
          </label>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              {form.widget_avatar_url ? (
                <img
                  src={form.widget_avatar_url}
                  alt="avatar preview"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  data-testid="legal-widget-avatar-preview"
                />
              ) : (
                <span className="text-xl text-gray-400">⚖️</span>
              )}
            </div>
            <input
              type="url"
              value={form.widget_avatar_url || ""}
              onChange={e => setForm(f => ({ ...f, widget_avatar_url: e.target.value }))}
              placeholder="https://miestudio.cl/logo.png"
              data-testid="legal-input-widget_avatar_url"
              className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">Si la URL no es válida o la imagen falla, se mostrará el ícono por defecto del widget.</p>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Mensaje de bienvenida</label>
          <textarea
            value={form.widget_welcome_message || ""}
            onChange={e => setForm(f => ({ ...f, widget_welcome_message: e.target.value }))}
            placeholder={`Hola, soy ${form.nombre_bot || "Renata"}, asistente de ${form.nombre_estudio || "tu estudio"}. ¿En qué puedo ayudarle?`}
            data-testid="legal-input-widget_welcome_message"
            rows={2}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <p className="mt-1 text-xs text-gray-500">Primera frase que verá el visitante al abrir el chat.</p>
        </div>
      </div>

      <div className="border-t pt-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-700">Código para tu sitio</h3>
          <button
            onClick={copySnippet}
            disabled={!slug}
            data-testid="legal-copy-widget-snippet-btn"
            className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {copied ? "✓ Copiado" : "Copiar snippet"}
          </button>
        </div>
        <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-x-auto font-mono leading-relaxed" data-testid="legal-widget-snippet">
{snippet}
        </pre>
        {!config?.has_claude_key && (
          <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs">
            <strong>Recordatorio:</strong> El widget no funcionará hasta que configures la Claude API Key del estudio en la pestaña Configuración.
          </div>
        )}
      </div>

      {/* Sticky save bar — explicit save so cambios de color/avatar persistan. */}
      <div className="sticky bottom-0 -mx-2 px-2 py-3 bg-white/95 backdrop-blur border-t flex flex-wrap items-center justify-end gap-3">
        <p className="text-xs text-gray-500 mr-auto">Guarda para que los colores, avatar y mensaje queden activos en tu sitio.</p>
        <button
          onClick={onSave}
          disabled={saving}
          data-testid="legal-save-widget-btn"
          className="px-5 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? "Guardando..." : "Guardar widget"}
        </button>
      </div>
    </div>
  );
}

// Mirror of KitPage.js layout, adapted to the Kit Legal vertical:
//  - Gmail App Password (encrypted at rest with LEGAL_ENCRYPTION_KEY on backend)
//  - Service Account based Calendar (no per-tenant OAuth)
//  - Lawyer list to monitor in PJUD emails
//  - Distinct color theme (purple/violet) to differentiate from Kit AutoBot
export default function LegalPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [saConfigEmail, setSaConfigEmail] = useState("");
  const [audiencias, setAudiencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningWatcher, setRunningWatcher] = useState(false);
  const [tab, setTab] = useState("config");
  const [openTooltip, setOpenTooltip] = useState(null);
  const [abogadoInput, setAbogadoInput] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState({
    nombre_bot: "Renata",
    nombre_estudio: "",
    abogados: [],
    gmail_user: "",
    gmail_app_password: "",
    calendar_id: "",
    vendedor_email: "",
    horario_atencion: "Lunes a Viernes 9:00 a 18:00",
    horario_citas: "Lunes a Viernes 10:00 a 17:00",
    base_conocimiento: "",
    openai_api_key: "",
    claude_api_key: "",
    widget_slug: "",
    widget_color: "#7c3aed",
    widget_text_color: "#ffffff",
    widget_secondary_color: "#a78bfa",
    widget_header_color: "",
    widget_avatar_url: "",
    widget_welcome_message: "",
    widget_enabled: true,
    activo: true,
  });

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const [cfgRes, audRes] = await Promise.all([
        fetch(`${API}/api/kit-legal/config`, { credentials: "include" }),
        fetch(`${API}/api/kit-legal/audiencias`, { credentials: "include" }),
      ]);
      const cfgData = await cfgRes.json();
      const audData = await audRes.json();
      setSaConfigEmail(cfgData.service_account_email || "");
      if (cfgData.config) {
        setConfig(cfgData.config);
        // Don't overwrite the password mask onto the form input — leave blank
        // so the user can leave it untouched on edit (backend keeps it).
        setForm(f => ({
          ...f,
          ...cfgData.config,
          gmail_app_password: "",
          openai_api_key: "",
          claude_api_key: "",
        }));
      }
      setAudiencias(audData.audiencias || []);
    } catch (e) {
      toast.error("Error cargando configuración");
    }
    setLoading(false);
  }

  async function runWatcherNow() {
    if (runningWatcher) return;
    setRunningWatcher(true);
    try {
      const res = await fetch(`${API}/api/kit-legal/run-watcher-now`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Error ejecutando el watcher");
      } else {
        const s = data.summary || {};
        const evs = s.events_created || 0;
        const fet = s.fetched || 0;
        toast.success(
          evs > 0
            ? `Revisión completa: ${evs} evento(s) creado(s) de ${fet} correo(s).`
            : `Revisión completa: ${fet} correo(s) revisado(s), sin nuevas audiencias.`
        );
        // Refresh the audiencias list to surface what was just created.
        try {
          const r2 = await fetch(`${API}/api/kit-legal/audiencias`, { credentials: "include" });
          const d2 = await r2.json();
          setAudiencias(d2.audiencias || []);
        } catch { /* ignore refresh failure */ }
      }
    } catch (e) {
      toast.error("Error de red ejecutando el watcher");
    }
    setRunningWatcher(false);
  }

  async function saveConfig() {
    if (!form.nombre_estudio.trim()) {
      toast.error("Falta el nombre del estudio");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/kit-legal/config`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Configuración guardada");
        setConfig(data.config);
        setForm(f => ({ ...f, gmail_app_password: "", openai_api_key: "" }));
      } else {
        toast.error(data.detail || "Error al guardar");
      }
    } catch (e) {
      toast.error("Error al guardar");
    }
    setSaving(false);
  }

  async function toggleActivo() {
    try {
      const res = await fetch(`${API}/api/kit-legal/config/toggle`, {
        method: "PATCH",
        credentials: "include",
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.activo ? "Kit Legal activado" : "Kit Legal desactivado");
        fetchData();
      }
    } catch (e) {
      toast.error("Error");
    }
  }

  function addAbogado() {
    const name = abogadoInput.trim();
    if (!name) return;
    if (form.abogados.length >= 50) {
      toast.error("Máximo 50 abogados");
      return;
    }
    // Case-insensitive dedupe (backend also dedupes)
    if (form.abogados.some(a => a.toLowerCase() === name.toLowerCase())) {
      toast.error("Ya está en la lista");
      return;
    }
    setForm(f => ({ ...f, abogados: [...f.abogados, name] }));
    setAbogadoInput("");
  }

  function removeAbogado(i) {
    setForm(f => ({ ...f, abogados: f.abogados.filter((_, idx) => idx !== i) }));
  }

  async function copySAEmail() {
    try {
      await navigator.clipboard.writeText(saConfigEmail);
      toast.success("Email copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  }

  async function testConnection() {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API}/api/kit-legal/test-connection`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          gmail_user: form.gmail_user,
          gmail_app_password: form.gmail_app_password,
          calendar_id: form.calendar_id,
        }),
      });
      const data = await res.json();
      setTestResult(data);
      if (data.imap_ok && data.calendar_ok) {
        toast.success("Gmail y Calendar conectados correctamente");
      } else if (data.imap_ok || data.calendar_ok) {
        toast.error("Conexión parcial — revisa los detalles abajo");
      } else {
        toast.error("Ambas conexiones fallaron — revisa los detalles abajo");
      }
    } catch (e) {
      toast.error("Error al probar la conexión");
    }
    setTesting(false);
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/dashboard')}
        data-testid="legal-back-to-dashboard"
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
      >
        ← Volver al Dashboard
      </button>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">⚖️ Kit Legal</h1>
          <p className="text-gray-500 text-sm mt-1">Asistente virtual para estudios jurídicos · detección automática de audiencias PJUD</p>
        </div>
        {config && (
          <button
            onClick={toggleActivo}
            data-testid="legal-toggle-btn"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              config.activo
                ? "bg-green-100 text-green-700 hover:bg-green-200"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {config.activo ? "✅ Activo" : "⏸️ Inactivo"}
          </button>
        )}
      </div>

      {/* Info banner: Gmail App Password + Service Account flow */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-5 mb-6" data-testid="legal-info-banner">
        <div className="flex items-start gap-3">
          <div className="text-2xl">🔐</div>
          <div className="flex-1 text-sm text-purple-900">
            <h3 className="text-base font-semibold mb-2">Cómo conectar tus servicios de Google</h3>
            <p className="mb-3 leading-relaxed">
              Kit Legal usa <strong>App Password de Gmail</strong> para leer correos de PJUD por IMAP, y un
              <strong> Service Account compartido de ACBotHub</strong> para crear eventos en tu Calendar — sin OAuth, sin
              compartir tu contraseña principal.
            </p>
            {saConfigEmail && (
              <div className="bg-white border border-purple-200 rounded-lg px-3 py-2 flex items-center gap-2 mb-3" data-testid="legal-sa-email-box">
                <span className="text-xs text-gray-500">Service Account:</span>
                <code className="text-xs font-mono text-purple-700 flex-1 truncate" data-testid="legal-sa-email">
                  {saConfigEmail}
                </code>
                <button
                  type="button"
                  onClick={copySAEmail}
                  data-testid="legal-copy-sa-btn"
                  className="text-xs bg-purple-600 text-white px-2.5 py-1 rounded hover:bg-purple-700 transition-colors"
                >
                  Copiar
                </button>
              </div>
            )}
            <p className="text-xs leading-relaxed">
              Compártele tu Google Calendar a ese email con permiso <strong>&quot;Hacer cambios en eventos&quot;</strong>
              (Calendar → Configuración del calendario → Compartir con personas específicas).
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        {[
          { id: "config", label: "⚙️ Configuración" },
          { id: "widget", label: "🌐 Widget Web" },
          { id: "audiencias", label: `📅 Audiencias detectadas (${audiencias.length})` },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`legal-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? "border-purple-600 text-purple-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* CONFIG TAB */}
      {tab === "config" && (
        <div className="bg-white rounded-xl border p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: "nombre_bot", label: "Nombre del bot", placeholder: "Renata" },
              { key: "nombre_estudio", label: "Nombre del estudio", placeholder: "Vargas & Zúñiga Abogados" },
              {
                key: "gmail_user",
                label: "Gmail del estudio",
                placeholder: "contacto@estudio.cl",
                helpText: "El correo desde donde Kit Legal lee notificaciones de PJUD por IMAP."
              },
              {
                key: "gmail_app_password",
                label: config?.has_gmail_password ? "App Password de Gmail (dejar en blanco para no cambiar)" : "App Password de Gmail",
                placeholder: config?.has_gmail_password ? "********" : "xxxx xxxx xxxx xxxx",
                type: "password",
                help: {
                  title: "Cómo generar un App Password (2 min)",
                  steps: [
                    { text: 'Activa la Verificación en 2 pasos: ', link: { href: 'https://myaccount.google.com/signinoptions/two-step-verification', label: 'myaccount.google.com/signinoptions/two-step-verification' } },
                    { text: 'Ve a Contraseñas de aplicaciones: ', link: { href: 'https://myaccount.google.com/apppasswords', label: 'myaccount.google.com/apppasswords' } },
                    { text: 'Escribe "Kit Legal ACBotHub" como nombre' },
                    { text: 'Copia los 16 caracteres y pégalos aquí' },
                  ],
                  warning: '🔐 Guardamos tu App Password ENCRIPTADO con AES (Fernet). Nunca lo mostramos de vuelta.'
                }
              },
              {
                key: "calendar_id",
                label: "ID de tu Google Calendar",
                placeholder: "tuestudio@gmail.com",
                helpText: "Suele ser tu correo de Gmail. Recuerda compartir el Calendar con el Service Account de arriba."
              },
              {
                key: "vendedor_email",
                label: "Email del estudio para notificaciones",
                placeholder: "contacto@estudio.cl",
                helpText: "Aquí llegan los avisos cuando Renata agenda una cita nueva o detecta una audiencia."
              },
              {
                key: "horario_atencion",
                label: "Horario de atención del bot",
                placeholder: "Lunes a Viernes 9:00 a 18:00"
              },
              {
                key: "horario_citas",
                label: "Horario válido para reuniones con clientes nuevos",
                placeholder: "Lunes a Viernes 10:00 a 17:00"
              },
            ].map(({ key, label, placeholder, type, help, helpText }) => (
              <div key={key}>
                <div className="flex items-center gap-1.5 mb-1">
                  <label className="block text-sm font-medium text-gray-700">{label}</label>
                  {help && (
                    <div className="relative inline-block group">
                      <button
                        type="button"
                        onClick={() => setOpenTooltip(openTooltip === key ? null : key)}
                        onBlur={() => setTimeout(() => setOpenTooltip(t => t === key ? null : t), 150)}
                        aria-label={`Ayuda para ${label}`}
                        data-testid={`legal-help-${key}`}
                        className="w-4 h-4 flex items-center justify-center rounded-full bg-gray-200 text-gray-600 text-[10px] font-bold hover:bg-purple-500 hover:text-white transition-colors cursor-help"
                      >
                        ?
                      </button>
                      <div
                        data-testid={`legal-tooltip-${key}`}
                        className={`absolute z-30 left-0 top-6 w-72 sm:w-80 bg-white rounded-lg shadow-xl border border-gray-200 p-3 text-xs text-gray-700 transition-opacity duration-150 ${
                          openTooltip === key
                            ? 'opacity-100 visible'
                            : 'opacity-0 invisible group-hover:opacity-100 group-hover:visible'
                        }`}
                      >
                        <p className="font-semibold text-gray-900 mb-2">{help.title}</p>
                        <ol className="list-decimal list-inside space-y-1.5">
                          {help.steps.map((s, i) => (
                            <li key={i} className="leading-snug">
                              {s.text}
                              {s.link && (
                                <a
                                  href={s.link.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-purple-600 hover:underline break-all"
                                >
                                  {s.link.label}
                                </a>
                              )}
                            </li>
                          ))}
                        </ol>
                        {help.warning && (
                          <p className="mt-2 pt-2 border-t border-gray-100 text-amber-700">{help.warning}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <input
                  type={type || "text"}
                  value={form[key] || ""}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  placeholder={placeholder}
                  data-testid={`legal-input-${key}`}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                {helpText && (
                  <p className="mt-1.5 text-xs text-gray-500 leading-relaxed">{helpText}</p>
                )}
              </div>
            ))}
          </div>

          {/* Lawyer list */}
          <div data-testid="legal-abogados-section">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Abogados del estudio a monitorear en correos de PJUD
            </label>
            <p className="text-xs text-gray-500 mb-3 leading-relaxed">
              Cuando llegue un correo de <code className="bg-gray-100 px-1 rounded">@pjud.cl</code> con un Excel adjunto,
              Kit Legal cruzará la columna de abogados contra esta lista para identificar audiencias que te corresponden.
              Usa el nombre completo tal como aparece en los oficios (ej: &quot;Pablo Vargas Soto&quot;).
            </p>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={abogadoInput}
                onChange={e => setAbogadoInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAbogado(); } }}
                placeholder="Nombre del abogado"
                data-testid="legal-abogado-input"
                className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <button
                type="button"
                onClick={addAbogado}
                data-testid="legal-add-abogado-btn"
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
              >
                + Agregar
              </button>
            </div>
            {form.abogados.length === 0 ? (
              <p className="text-xs text-gray-400 italic">Sin abogados agregados todavía.</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="legal-abogados-list">
                {form.abogados.map((name, i) => (
                  <span
                    key={i}
                    data-testid={`legal-abogado-chip-${i}`}
                    className="inline-flex items-center gap-1.5 bg-purple-100 text-purple-800 rounded-full px-3 py-1 text-xs"
                  >
                    {name}
                    <button
                      type="button"
                      onClick={() => removeAbogado(i)}
                      data-testid={`legal-remove-abogado-${i}`}
                      className="text-purple-500 hover:text-purple-900 font-bold leading-none"
                      aria-label={`Eliminar ${name}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <span className="inline-flex items-center text-xs text-gray-400 px-1">
                  {form.abogados.length} / 50
                </span>
              </div>
            )}
          </div>

          {/* Knowledge base — injected into Renata's WhatsApp system prompt */}
          <div data-testid="legal-knowledge-base-section">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Base de conocimiento del estudio
            </label>
            <p className="text-xs text-gray-500 mb-2 leading-relaxed">
              Texto libre que Renata usará al chatear por WhatsApp con clientes nuevos. Incluye servicios,
              áreas legales, trayectoria, dirección de la oficina, precios o cualquier dato que el bot deba
              conocer. Sé claro y conciso — el LLM lo lee tal cual lo escribes.
            </p>
            <textarea
              value={form.base_conocimiento || ""}
              onChange={e => setForm(f => ({ ...f, base_conocimiento: e.target.value }))}
              rows={10}
              placeholder={
                "Ejemplo:\n\nÁREAS LEGALES: Civil, familiar, laboral, comercial.\n\nTRAYECTORIA: 15 años de experiencia. " +
                "Socios con magíster en derecho procesal.\n\nDIRECCIÓN: Antonio Varas 687, oficina 1010, Temuco.\n\n" +
                "PRECIOS: La consulta inicial es gratuita. Honorarios definidos en la reunión."
              }
              data-testid="legal-input-base_conocimiento"
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <p className="mt-1.5 text-xs text-gray-500">
              {(form.base_conocimiento || "").length} caracteres
            </p>
          </div>

          {/* Claude API Key — OBLIGATORIA. Cada estudio debe traer su propia key. */}
          <div data-testid="legal-claude-key-section" className={(!config?.has_claude_key && !form.claude_api_key) ? "p-4 rounded-lg border-2 border-red-300 bg-red-50/40" : ""}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Claude API Key del estudio{" "}
              <span className="text-xs font-semibold text-red-600">(obligatoria)</span>
            </label>
            {(!config?.has_claude_key && !form.claude_api_key) && (
              <div className="mb-3 p-3 rounded-lg bg-red-100 border border-red-200 text-red-800 text-xs leading-relaxed" data-testid="legal-claude-key-warning">
                <strong>Falta la Claude API Key.</strong> Sin esta clave Renata no puede funcionar — ni por WhatsApp ni por el widget web.
                Cada estudio debe traer su propia clave de Anthropic (no se comparte con otros clientes).
                Obtenla en <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="underline font-medium">console.anthropic.com/settings/keys</a>
                {" "}y pégala abajo.
              </div>
            )}
            <p className="text-xs text-gray-500 mb-2 leading-relaxed">
              Pega aquí la API Key de Anthropic de tu estudio
              (<code className="bg-gray-100 px-1 rounded">sk-ant-…</code>). Es la clave que Renata usará para
              conversar con tus clientes. Se guarda encriptada y nunca se comparte.
            </p>
            <input
              type="password"
              value={form.claude_api_key || ""}
              onChange={e => setForm(f => ({ ...f, claude_api_key: e.target.value }))}
              placeholder={config?.has_claude_key ? "******** (ya configurada — deja vacío para no cambiar)" : "sk-ant-api03-..."}
              data-testid="legal-input-claude_api_key"
              autoComplete="new-password"
              className={`w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500 ${(!config?.has_claude_key && !form.claude_api_key) ? "border-red-300" : ""}`}
            />
            {config?.claude_api_key_masked && (
              <p className="mt-1.5 text-xs text-gray-500">
                Actual: <code className="bg-gray-100 px-1 rounded">{config.claude_api_key_masked}</code>
              </p>
            )}
          </div>

          <button
            onClick={testConnection}
            disabled={testing}
            data-testid="legal-test-connection-btn"
            className="w-full bg-white border-2 border-purple-300 text-purple-700 py-2 rounded-lg font-medium hover:bg-purple-50 disabled:opacity-50 transition-colors"
          >
            {testing ? "Probando conexión..." : "🔍 Probar conexión con Gmail y Calendar"}
          </button>

          {testResult && (
            <div className="space-y-2" data-testid="legal-test-result">
              <div
                className={`rounded-lg px-4 py-3 text-sm border ${
                  testResult.imap_ok ? "bg-green-50 border-green-200 text-green-900" : "bg-red-50 border-red-200 text-red-900"
                }`}
                data-testid="legal-test-imap-row"
              >
                <span className="font-semibold">{testResult.imap_ok ? "✅" : "❌"} Gmail IMAP:</span>{" "}
                {testResult.imap_message}
              </div>
              <div
                className={`rounded-lg px-4 py-3 text-sm border ${
                  testResult.calendar_ok ? "bg-green-50 border-green-200 text-green-900" : "bg-red-50 border-red-200 text-red-900"
                }`}
                data-testid="legal-test-calendar-row"
              >
                <span className="font-semibold">{testResult.calendar_ok ? "✅" : "❌"} Google Calendar:</span>{" "}
                {testResult.calendar_message}
              </div>
            </div>
          )}

          <button
            onClick={saveConfig}
            disabled={saving}
            data-testid="legal-save-btn"
            className="w-full bg-purple-600 text-white py-2 rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 transition-colors"
          >
            {saving ? "Guardando..." : "Guardar configuración"}
          </button>
        </div>
      )}

      {/* WIDGET TAB */}
      {tab === "widget" && (
        <WidgetTab form={form} setForm={setForm} config={config} onSave={saveConfig} saving={saving} />
      )}

      {/* AUDIENCIAS TAB */}
      {tab === "audiencias" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-medium text-gray-700">Audiencias detectadas</h3>
              <p className="text-xs text-gray-500">El watcher revisa Gmail cada 5 minutos. También puedes forzar una revisión ahora.</p>
            </div>
            <button
              onClick={runWatcherNow}
              disabled={runningWatcher || !config?.activo}
              data-testid="legal-run-watcher-now-btn"
              className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {runningWatcher ? "Revisando..." : "Buscar audiencias ahora"}
            </button>
          </div>
          <div className="bg-white rounded-xl border overflow-hidden" data-testid="legal-audiencias-tab">
          {audiencias.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <div className="text-4xl mb-2">⚖️</div>
              <p className="mb-1">Aún no se han detectado audiencias.</p>
              <p className="text-xs">
                El watcher revisa tu Gmail cada 5 minutos buscando correos de pjud.cl.
                Las audiencias detectadas y los eventos creados en Calendar aparecerán aquí.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {["Detectado", "RIT", "Tipo", "Fecha", "Hora", "Abogado", "Evento Calendar"].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {audiencias.map((a, i) => (
                    <tr key={i} className="hover:bg-gray-50" data-testid={`legal-audiencia-row-${i}`}>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                        {a.detectado_at ? new Date(a.detectado_at).toLocaleString('es-CL') : "-"}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{a.rit || "-"}</td>
                      <td className="px-4 py-3">{a.tipo || "-"}</td>
                      <td className="px-4 py-3">{a.fecha || "-"}</td>
                      <td className="px-4 py-3">{a.hora || "-"}</td>
                      <td className="px-4 py-3 text-gray-600">{a.abogado_match || "-"}</td>
                      <td className="px-4 py-3">
                        {a.calendar_event_id ? (
                          <span className="text-green-600 text-xs">✅ Creado</span>
                        ) : (
                          <span className="text-amber-600 text-xs">⏳ Pendiente</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
}
